import React, { useState } from 'react';
import moment from 'moment';
import { FormInput } from 'components/form';
import { DateFormats } from 'constants/common';
import { blueGrey, grey } from '@mui/material/colors';
import { useOptimisticState } from 'hooks/useOptimistic';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { EventNote } from '../../types';
import { useCalendar } from '../../api';

interface Props {
  eventId: string;
  loading: boolean;
  apiNotes: EventNote[];
}

const SYNTHETIC_ID = '-100' as const;

export const EventNotesContent = ({ eventId, loading, apiNotes }: Props) => {
  const { handleAddEventNote, isAddingEventNote } = useCalendar();

  const [notes, setNotes] = useOptimisticState(apiNotes);
  const [newNote, setNewNote] = useState<string>('');

  const onAddEventNote = () => {
    const note: EventNote = {
      id: SYNTHETIC_ID,
      content: newNote,
      createdById: '',
      eventActivityId: eventId,
      createdDateTime: new Date().toISOString(),
    };

    setNotes(
      handleAddEventNote(eventId, newNote).then(value => (value ? [value, ...notes] : notes)),
      [note, ...notes]
    );

    setNewNote('');
  };

  return (
    <Stack gap="0.5rem">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap="0.5rem">
          <Typography variant="subtitle2" component="span">
            Notes
          </Typography>

          <RenderWithCondition condition={loading}>
            <CircularProgress size={16} />
          </RenderWithCondition>
        </Stack>

        <Button loading={isAddingEventNote} disabled={!newNote} onClick={onAddEventNote}>
          Save
        </Button>
      </Stack>

      <Stack gap="0.5rem" overflow="auto">
        <FormInput
          multiline
          minRows={4}
          maxRows={4}
          value={newNote}
          variant="outlined"
          placeholder="Enter note"
          onChange={value => setNewNote(value)}
        />

        <Stack gap="0.5rem">
          {notes.map(note => (
            <Box
              key={note.id}
              sx={{
                borderRadius: '0.5rem',
                padding: '0.5rem 0.75rem',
                border: `1px solid ${blueGrey[100]}`,
                backgroundColor: isAddingEventNote && note.id === SYNTHETIC_ID ? grey[200] : grey[50],
              }}
            >
              <Stack direction="row" alignItems="center" gap="0.5rem">
                <Typography variant="subtitle2" color="textSecondary">
                  {note.id === SYNTHETIC_ID
                    ? 'Processing...'
                    : moment(note.createdDateTime).format(DateFormats.DateTimeShort24Hour)}
                </Typography>
              </Stack>

              <Typography variant="body2">{note.content}</Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};
